import React, { useState } from 'react';
import { Tip, TipProvider } from '@ofself/shell';
import { ArrowRight } from 'lucide-react';
import { PersonFace, GroupFaceIcon, PluginFace } from './faces';
import { AppFace } from './cells';
import { CircleHexagonGrid } from './glyphs';
import { useIsPhone } from './PhoneNav';
import { fmt } from '../../nucleus/format';

// THE VAULT — Nucleus's front door: a self drawn as a square cell on the dot
// grid, its parts pressed as navigation (design.oS, "The cell"):
//
//   internal  the nucleus: the one filled square, your Identity (the graph)
//   gates     the membrane: a solid square line around it, set with short
//             posts all the way round, a wall with gates in it. Its name and
//             its figures sit below it.
//   external  four sheets just outside the membrane, one to a side: People
//             (top), Groups (right), Apps (bottom), Plugins (left). A sheet is
//             a small window onto its section: its name and count (a door to
//             the section), then its items listed, each a face and its name and
//             a door to that item (`onItem(sectionId, item)`), and "+N more"
//             when more are there than fit. Every sheet is as long as the
//             wall's square is wide, so the cell is a cross, even on all sides.
//
// Each part is { id, word, n, lines } (the gates part also carries `open`):
// the number is the headline, `lines` up to two short facts under it
// ({ text, tone, why? }; tone 'warn' | 'live' marks something that wants you).
// `onPress(id)` opens it; a sheet's waiting line presses `onPress(id, why)`,
// its own `why` when it names one ("runs"), else 'warn'. A figure still being
// read is undefined: drawn blank.
//
// The dot grid shows everywhere between the parts. Square throughout; nothing
// moves under the pointer: a hovered part takes its layer's tint, nothing
// else changes. It fades in once on arrival, and stands still for
// prefers-reduced-motion.

const C = 500;
const NUCLEUS = 116;           // the nucleus's half-width
const MEMBRANE = 206;          // the membrane line's half-width
const BAND = 52;               // the membrane's pressable reach either side of its line
const SHEET_IN = 300;          // a sheet's inner edge, from the centre
// clear ground between the layers: nucleus → band 38, band → sheets 42
const DEPTH = 214;             // every sheet's depth, outward from its inner edge
const SHEET_OUT = SHEET_IN + DEPTH;
// every sheet is as long as the wall's square is wide, and stands square to it:
// the cell is a cross, the same on all four sides
const SHEET_HALF = MEMBRANE + BAND;
const VIEW = 2 * (SHEET_OUT + 16); // the drawing, square, centred on the cell

const figure = (n) => (n == null ? '' : typeof n === 'number' ? fmt(n) : n);
const rect = (x0, y0, x1, y1) => `M${C + x0},${C + y0} H${C + x1} V${C + y1} H${C + x0} Z`;
const square = (h) => rect(-h, -h, h, h);

// the four sheets, one to a side
const SHEETS = [
    { cell: [-SHEET_HALF, -SHEET_OUT, SHEET_HALF, -SHEET_IN] },  // top
    { cell: [SHEET_IN, -SHEET_HALF, SHEET_OUT, SHEET_HALF] },    // right
    { cell: [-SHEET_HALF, SHEET_IN, SHEET_HALF, SHEET_OUT] },    // bottom
    { cell: [-SHEET_OUT, -SHEET_HALF, -SHEET_IN, SHEET_HALF] },  // left
];
const HEAD = 52;      // a sheet's head: its name and count
const PAD = 14;       // the sheet's inset around its list
const TILE = 44;      // one item: its face alone, named on hover

// the membrane: a solid square, set with short posts at an even pitch along
// each side, standing outward
const POST = 9;
const PITCH = 34;
function membranePosts() {
    const h = MEMBRANE;
    const n = Math.max(2, Math.round((2 * h) / PITCH));
    const step = (2 * h) / n;
    const d = [];
    for (let i = 1; i < n; i += 1) {
        const t = -h + i * step;
        d.push(`M${C + t},${C - h} V${C - h - POST}`, `M${C + t},${C + h} V${C + h + POST}`);
        d.push(`M${C - h},${C + t} H${C - h - POST}`, `M${C + h},${C + t} H${C + h + POST}`);
    }
    return d.join(' ');
}

const linesOf = (part) => (part.lines || []).filter((l) => l && l.text);
const say = (part) => [part.word, part.n != null ? figure(part.n) : null, ...linesOf(part).map((l) => l.text)].filter(Boolean).join(', ');
function Lines({ part, x, y, gap = 18 }) {
    return linesOf(part).slice(0, 2).map((l, i) => (
        <text key={l.text} className={`nx-vault__sub${l.tone ? ` is-${l.tone}` : ''}`} x={x} y={y + i * gap} textAnchor="middle">{l.text}</text>
    ));
}

function Door({ id, d, label, className = '', order = 0, hover, onHover, onPress, children }) {
    const press = () => onPress?.(id);
    return (
        <g
            className={`nx-vault__door ${className}${hover === id ? ' is-hover' : ''}`}
            style={{ '--order': order }}
            role="button" tabIndex={0} aria-label={label}
            onMouseEnter={() => onHover(id)} onMouseLeave={() => onHover(null)}
            onFocus={() => onHover(id)} onBlur={() => onHover(null)}
            onClick={press} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); press(); } }}
        >
            <path className="nx-vault__shape" d={d} />
            {children}
        </g>
    );
}

const FACE = 36;
function ItemFace({ kind, item }) {
    if (kind === 'person') return <PersonFace url={item.url} size={FACE} />;
    if (kind === 'group') return <GroupFaceIcon url={item.url} size={FACE} />;
    if (kind === 'app') return <span className="nx-sheet__appface"><AppFace app={item.app} size={FACE} /></span>;
    return <PluginFace size={FACE} />;
}

// one outside sheet: a window onto its section
function Sheet({ part, spec, order, onPress, onItem }) {
    const [x0, y0, x1, y1] = spec.cell;
    const w = x1 - x0; const h = y1 - y0;
    const warn = linesOf(part).filter((l) => l.tone);
    const cols = Math.max(1, Math.floor((w - 2 * PAD) / TILE));
    const rows = Math.max(1, Math.floor((h - HEAD - PAD - warn.length * 28) / TILE));
    const room = rows * cols;
    const items = part.items;
    const total = typeof part.n === 'number' ? part.n : items?.length || 0;
    const shown = items ? (items.length > room ? items.slice(0, room - 1) : items) : [];
    const more = items && items.length > room ? total - shown.length : 0;
    return (
        <g className="nx-vault__sheet" style={{ '--order': order }}>
            <foreignObject x={C + x0} y={C + y0} width={w} height={h}>
                {/* the whole sheet opens its section; a face inside opens its own item */}
                <div className="nx-sheet" xmlns="http://www.w3.org/1999/xhtml" role="group" aria-label={part.word}
                    onClick={(e) => { if (!e.target.closest('button')) onPress?.(part.id); }}>
                    <button type="button" className="nx-sheet__head" onClick={() => onPress?.(part.id)} aria-label={say(part)}>
                        <span className="nx-sheet__word">{part.word}<ArrowRight className="nx-sheet__go" aria-hidden="true" /></span>
                        <span className="nx-sheet__n">{figure(part.n)}</span>
                    </button>
                    {warn.map((l) => (
                        <button key={l.text} type="button" className={`nx-sheet__warn is-${l.tone}`} onClick={() => onPress?.(part.id, l.why || 'warn')}>{l.text}</button>
                    ))}
                    {items && items.length === 0 && <p className="nx-sheet__empty">{part.empty || 'Nothing here yet'}</p>}
                    {/* a sheet with no list says its plain lines instead */}
                    {!items && linesOf(part).filter((l) => !l.tone).map((l) => <p key={l.text} className="nx-sheet__empty">{l.text}</p>)}
                    {shown.length > 0 && (
                        <div className="nx-sheet__tiles" style={{ gridTemplateColumns: `repeat(${cols}, ${TILE}px)`, gridAutoRows: `${TILE}px` }}>
                            {shown.map((it) => (
                                <Tip key={it.id} label={it.label}>
                                    <button type="button" className="nx-sheet__tile" onClick={() => onItem?.(part.id, it)} aria-label={it.label}>
                                        <ItemFace kind={part.kind} item={it} />
                                    </button>
                                </Tip>
                            ))}
                            {more > 0 && (
                                <button type="button" className="nx-sheet__tile is-more" onClick={() => onPress?.(part.id)} aria-label={`All ${figure(total)} ${String(part.word).toLowerCase()}`}>
                                    +{more}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </foreignObject>
        </g>
    );
}

// ON A PHONE the cell cannot be drawn readably at that width, so it stands as
// the same parts stacked: you, the membrane, then each sheet with its faces.
const PHONE_ITEMS = 15;
function PhoneVault({ core, gates, outside, onPress, onItem, label = 'Your vault' }) {
    const c = core[0];
    const gate = gates[0];
    return (
        <TipProvider delayDuration={0} skipDelayDuration={0}>
            <div className="nx-pvault" role="group" aria-label={label}>
                {c && (
                    <button type="button" className="nx-pvault__core" onClick={() => onPress?.(c.id)} aria-label={say(c)}>
                        <CircleHexagonGrid className="nx-pvault__glyph" size={24} strokeWidth={1.4} />
                        <span className="nx-pvault__word">{c.word}</span>
                        <span className="nx-pvault__n">{figure(c.n)}</span>
                        {linesOf(c).slice(0, 2).map((l) => <span key={l.text} className={`nx-pvault__sub${l.tone ? ` is-${l.tone}` : ''}`}>{l.text}</span>)}
                    </button>
                )}
                {gate && (
                    <button type="button" className="nx-pvault__gates" onClick={() => onPress?.(gate.id)} aria-label={say(gate)}>
                        <span className="nx-pvault__word">{gate.word}</span>
                        <span className="nx-pvault__sub">{linesOf(gate).map((l) => l.text).join('  ·  ')}</span>
                    </button>
                )}
                {outside.slice(0, 4).map((o) => {
                    const items = o.items || [];
                    const total = typeof o.n === 'number' ? o.n : items.length;
                    const shown = items.length > PHONE_ITEMS ? items.slice(0, PHONE_ITEMS - 1) : items;
                    const more = total - shown.length;
                    const warn = linesOf(o).filter((l) => l.tone);
                    return (
                        <div key={o.id} className="nx-sheet nx-pvault__sheet" role="group" aria-label={o.word}
                            onClick={(e) => { if (!e.target.closest('button')) onPress?.(o.id); }}>
                            <button type="button" className="nx-sheet__head" onClick={() => onPress?.(o.id)} aria-label={say(o)}>
                                <span className="nx-sheet__word">{o.word}<ArrowRight className="nx-sheet__go" aria-hidden="true" /></span>
                                <span className="nx-sheet__n">{figure(o.n)}</span>
                            </button>
                            {warn.map((l) => <button key={l.text} type="button" className={`nx-sheet__warn is-${l.tone}`} onClick={() => onPress?.(o.id, l.why || 'warn')}>{l.text}</button>)}
                            {o.items && items.length === 0 && <p className="nx-sheet__empty">{o.empty || 'Nothing here yet'}</p>}
                            {shown.length > 0 && (
                                <div className="nx-sheet__items nx-pvault__items">
                                    {shown.map((it) => (
                                        <Tip key={it.id} label={it.label}>
                                            <button type="button" className="nx-sheet__item" onClick={() => onItem?.(o.id, it)} aria-label={it.label}>
                                                <ItemFace kind={o.kind} item={it} />
                                            </button>
                                        </Tip>
                                    ))}
                                    {more > 0 && <button type="button" className="nx-sheet__item is-more" onClick={() => onPress?.(o.id)} aria-label={`All ${figure(total)}`}>+{more}</button>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </TipProvider>
    );
}

export default function Vault(props) {
    const phone = useIsPhone();
    return phone ? <PhoneVault {...props} core={props.core || []} gates={props.gates || []} outside={props.outside || []} /> : <CellVault {...props} />;
}

function CellVault({ core = [], gates = [], outside = [], onPress, onItem, label = 'Your vault' }) {
    const [hover, setHover] = useState(null);
    const c = core[0];
    const gate = gates[0];
    const common = { hover, onHover: setHover, onPress };

    return (
        <TipProvider delayDuration={0} skipDelayDuration={0}>
        <svg className="nx-vault" viewBox={`${C - VIEW / 2} ${C - VIEW / 2} ${VIEW} ${VIEW}`} preserveAspectRatio="xMidYMid meet" role="group" aria-label={label}>

            {/* external: a sheet to each side, People at the top, then clockwise */}
            {outside.slice(0, 4).map((o, i) => (
                <Sheet key={o.id} part={o} spec={SHEETS[i]} order={2 + i} onPress={onPress} onItem={onItem} />
            ))}

            {/* gates: the membrane, a gap for every open gate; its name below, its figures above */}
            {gate && (
                <Door id={gate.id} d={`${square(MEMBRANE + BAND)} ${square(MEMBRANE - BAND)}`} label={say(gate)} className="is-gates" order={1} {...common}>
                    <path className="nx-vault__membrane" d={square(MEMBRANE)} />
                    <path className="nx-vault__posts" d={membranePosts()} />
                    {/* its name inside the line, its figures outside it, both within the band */}
                    <text className="nx-vault__ring-word" x={C} y={C + MEMBRANE - 18} textAnchor="middle">
                        {String(gate.word).split(/\s*&\s*/).join('  ·  ')}
                    </text>
                    {linesOf(gate).length > 0 && (
                        <text x={C} y={C + MEMBRANE + 36} textAnchor="middle">
                            {linesOf(gate).map((l, i) => (
                                <tspan key={l.text} className={`nx-vault__ring-sub${l.tone ? ` is-${l.tone}` : ''}`}>{`${i ? '  ·  ' : ''}${l.text}`}</tspan>
                            ))}
                        </text>
                    )}
                </Door>
            )}

            {/* internal: you, the one filled square */}
            {c && (
                <Door id={c.id} d={square(NUCLEUS)} label={say(c)} className="is-internal" order={0} {...common}>
                    {/* the identity graph's own glyph, as the rail draws it */}
                    <CircleHexagonGrid className="nx-vault__glyph" x={C - 14} y={C - 88} size={28} strokeWidth={1.4} />
                    <text className="nx-vault__word" x={C} y={C - 38} textAnchor="middle">{c.word}</text>
                    <text className="nx-vault__n" x={C} y={C + 34} textAnchor="middle">{figure(c.n)}</text>
                    <Lines part={c} x={C} y={C + 66} gap={22} />
                </Door>
            )}
        </svg>
        </TipProvider>
    );
}
